import type { FC, ReactNode } from 'react'
import type { NavLink } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { Logo } from '@/components/generals/logo'
import { useParallax } from '@/hooks/use-parallax'

import { FOOTER_PRODUCT_LINKS, FOOTER_COMPANY_LINKS } from '@/constants/navigation'

interface FooterColumnProps {
    title: string
    links: Array<NavLink>
    className?: string
}

const FooterColumn: FC<FooterColumnProps> = ({ title, links, className }): ReactNode => {
    return (
        <nav aria-label={title} className={className}>
            <h2 className='text-[15px] font-medium text-[#f7f5ee]'>
                {title}
            </h2>

            <ul className='mt-3 space-y-2'>
                {links.map(link => (
                    <li key={link.href}>
                        <Link
                            href={link.href}
                            className='inline-block text-[15px] text-[#e4e1d4]/80 transition-[color,translate] duration-300 ease-out hover:translate-x-1 hover:text-white motion-reduce:hover:translate-x-0 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
                        >
                            {link.label}
                        </Link>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

export const SiteFooter: FC = (): ReactNode => {
    const landscapeRef = useParallax<HTMLDivElement>(0.08)

    return (
        <footer className='relative'>
            <div className='relative h-[clamp(560px,58vw,860px)] overflow-hidden'>
                <div ref={landscapeRef} className='absolute inset-x-0 -top-[12%] -bottom-[12%] will-change-[translate]'>
                    <Image
                        src='/images/landscape.jpg'
                        alt='A painted stone cottage on a hill above meadows of wildflowers at golden hour'
                        fill
                        sizes='100vw'
                        className='object-cover object-[72%_center]'
                    />
                </div>

                <div aria-hidden='true' className='absolute inset-x-0 top-0 h-[42%] bg-gradient-to-b from-background via-background/75 to-transparent' />

                <div aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[72%] bg-gradient-to-t sm:h-[58%] from-[#1d2418]/90 via-[#1d2418]/45 to-transparent' />

                <div className='absolute inset-x-0 bottom-0'>
                    <div className='mx-auto grid max-w-6xl grid-cols-2 gap-x-8 gap-y-10 px-6 pb-12 lg:grid-cols-[1.6fr_1fr_1fr] lg:px-8'>
                        <div className='reveal col-span-2 lg:col-span-1'>
                            <Link
                                href='/'
                                aria-label='Closewell home'
                                className='group inline-block rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70'
                            >
                                <Logo className='text-[#f7f5ee]' />
                            </Link>

                            <p className='mt-4 max-w-xs text-[15px] leading-relaxed text-[#e4e1d4]/85'>
                                Calm, careful software for the people who help other people move home.
                            </p>
                        </div>

                        <FooterColumn title='Product' links={FOOTER_PRODUCT_LINKS} className='reveal reveal-d1' />

                        <FooterColumn title='Company' links={FOOTER_COMPANY_LINKS} className='reveal reveal-d2' />
                    </div>
                </div>
            </div>

            <div className='bg-moss text-[#cfccbe]'>
                <div className='mx-auto flex max-w-6xl flex-col gap-3 px-6 py-5 text-sm sm:flex-row sm:items-center sm:justify-between lg:px-8'>
                    <p>
                        © 2026 Closewell. Built with care.
                    </p>

                    <ul className='flex flex-wrap gap-x-6 gap-y-2'>
                        <li>
                            <Link href='/privacy' className='transition-colors hover:text-white'>
                                Privacy
                            </Link>
                        </li>

                        <li>
                            <Link href='/#safety' className='transition-colors hover:text-white'>
                                How we keep you in control
                            </Link>
                        </li>

                        <li>
                            <Link href='/contact' className='transition-colors hover:text-white'>
                                Contact
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
        </footer>
    )
}
